import {IHttpService} from "angular";
import {ChartingPreferences, FeatureNames, Preferences, UserConfig, WidgetPreference} from "../../types/UserConfig";
import {Optional} from "../../js/util/Optional";
import {Branding} from "../../types/Branding";
import {Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {CurrentUser} from "./models/CurrentUser";

export class CurrentUserService {

    public static id = "currentUserService";

    private readonly current$ = new ReplaySubject<CurrentUser>(1);
    private readonly refresh$ = new Subject<void>();
    private readonly baseUrl = "/tradefloor/api/user";

    /** @ngInject */
    public constructor(private $http: IHttpService, private userConfig: UserConfig) {
        this.init();
    }

    private init() {
        this.configChanged();
        this.watchRefresh();
    }

    private watchRefresh() {
        this.refresh$
            .debounceTime(100)
            .flatMap(() => this.userConfig.refresh())
            .subscribe(() => this.configChanged());
    }

    public configChanged() {
        const user = CurrentUser.from(this.userConfig);
        this.current$.next(user);
        this.refreshFeatureRelatedConfigs();
    }

    private refreshFeatureRelatedConfigs() {
        const hasEquityOrders = this.userConfig.features["SCREEN_EQUITY_ORDERS"];
        const hasOptionOrders = this.userConfig.features["SCREEN_OPTION_ORDERS"];

        this.userConfig.viewOrdersEnabled = hasEquityOrders || hasOptionOrders || false;
        this.userConfig.ordersEnabled = this.userConfig.features["FEATURE_ORDER_ENTRY"] || false;
        this.userConfig.livePrices = this.userConfig.features["FEATURE_LIVE_PRICES"] ||
            this.userConfig.features["FEATURE_DEMO_PRICES"] || false;
    }

    public async updatePreference<K extends keyof Preferences>(key: K, value: Preferences[K]): Promise<void> {
        const url = `${this.baseUrl}/preferences/${key}`;
        const data = JSON.stringify(value);
        await this.$http.put(url, data);

        const response = await this.$http.get<ChartingPreferences>(url);
        const prefs = response.data!;
        const updated = {[key]: prefs};
        const preferences = Object.assign({}, this.userConfig.preferences, updated);
        Object.assign(this.userConfig, {preferences});
        this.configChanged();
    }

    public async updateChartPreference(value: ChartingPreferences): Promise<void> {
        return await this.updatePreference("charting", value);
    }

    public portfolioWidgetPreference(): Array<WidgetPreference> | undefined {
        return this.preference("portfolio.widgets");
    }

    public chartingPreference(): ChartingPreferences | undefined {
        return this.preference("charting");
    }

    public preference<K extends keyof Preferences>(key: K): Preferences[K] | undefined {
        return this.userConfig.preferences[key];
    }

    public get userId(): number {
        return this.userConfig.id;
    }

    public get user(): CurrentUser {
        return CurrentUser.from(this.userConfig);
    }

    public get current(): Observable<CurrentUser> {
        return this.current$.observeOn(Scheduler.async);
    }

    public refreshConfig() {
        this.refresh$.next();
    }

    public hasWatchedTmcVideo() {
        //TODO: To enable feature whether user have watched tmc or not please enable this. currently set to true as default
        // Only Client Role needs to view the video, and only once
        // return this.userConfig.client == null || this.userConfig.client.watchedTmcVideo;

        return true;
    }

    public async setWatchedTmcVideo(): Promise<void> {
        if (this.userConfig.client != null) {
            const url = `${this.baseUrl}/${this.userConfig.id}/tmc-video`;
            await this.$http.post(url, null);
            this.userConfig.client.watchedTmcVideo = true;
            this.refreshConfig();
        }
    }

    public hasPassedMarginQuiz(): boolean {
        return Optional.of(this.userConfig)
                       .map(uc => uc.client)
                       .map(c => c.passedOptionsQuiz)
                       .orElse(false);
    }

    public hasSwiftApplication(): boolean {
        return Optional.of(this.userConfig)
                       .map(c => c.hasSwiftApplication)
                       .orElse(false);
    }

    public hasMfaEnabled(): boolean {
        return this.userConfig.isMfaEnabled;
    }

    public hasFeature(...features: Array<FeatureNames>): boolean {
        const userFeatures = this.userConfig.features;
        if (this.userConfig.backOffice === "PHILLIP") {
            userFeatures["FEATURE_ADV_CHARTING"] = true;
        }
        return features.some((feature) => userFeatures[feature] === true);
    }

    public isSuperAdmin(): boolean {
        return this.userConfig.super != null;
    }

    public isSupport(): boolean {
        return this.userConfig.support != null;
    }

    public isRiskManager(): boolean {
        return this.userConfig.riskManager != null;
    }

    public isAdviser(): boolean {
        return this.userConfig.adviser != null;
    }

    public isClient(): boolean {
        return this.userConfig.client != null;
    }

    public isGame(): boolean {
        return this.userConfig.branding === "GAME";
    }

    public isBranding(branding: Branding): boolean {
        return this.userConfig.branding === branding;
    }

    public isOauthAuthenticated(): boolean {
        const oauthAuthenticatedBroker = [Branding.OPEN_MARKETS,Branding.TF,Branding.BELLMONT,Branding.FUTURE_ASSIST,Branding.ALLFIN,Branding.WAIMAK];
        return oauthAuthenticatedBroker.includes(this.userConfig.branding);
    }

    public isLogin() {
        return this.userConfig.loggedIn;
    }

    public sessionExpired() {
        if (this.userConfig.loggedIn) {
            // This is terrible and dirty, but we're beyond the point where tech debt matters...
            this.userConfig.loggedIn = false;
            this.configChanged();
        }
    }

    /**
     * @deprecated - Dirty hack to make this work right now, shouldn't really be used, but the photo upload stuff
     *               needs it
     */
    public updateUserImage(imageUrl: string) {
        this.userConfig.imageUrl = imageUrl;
        this.configChanged();
    }
}
